import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { multiStep } from '../../MultiStepWizard/multiStep'
import { AIJobStatus, SelectedImage } from 'types'
import { Form } from 'components/Wizards/Form/Form'
import Styles from '../DesignMarketplaceWizard.module.scss'
import { Formik, useFormikContext } from 'formik'
import cx from 'classnames'
import { useEffect, useState } from 'react'
import { useWizardSchema } from '../../Wizard/WizardContext'
import { MultiStepSchema } from '../../schemas/Types'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { collectResults } from 'utils/query'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import * as CSS from 'csstype'
import { VisualElement } from 'api/clients/VisualElementClient'
import { compact, uniqBy } from 'lodash'
import { Actions } from './Actions'
import { Selection } from '@react-types/shared'
import { FilePathFormikField } from '../../FilePathFormikField/FilePathFormikField'
import { TemplatePreview } from './TemplatePreview'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { useRecentVisualTemplates } from 'hooks/useRecentVisualTemplates'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

export interface Values {
  filename: string
}
interface Props extends StepProps {
  selectedImage: SelectedImage
}

export const SelectTemplate = multiStep<Props>((props) => {
  const { schema, onSubmit, initialValues, currentStep, stepIndex } = props
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const { mutate, data } = useCreateFieldsetCompletion()
  const [initialNameLoaded, setInitialNameLoaded] = useState(false)

  // Generate and fetch the file name
  useEffect(() => {
    if (!initialValues.initial_media_file_id || currentStep !== stepIndex) return
    const fields = ['filename']

    mutate(
      {
        fields,
        completedFields: {
          initial_media_file_id: initialValues.initial_media_file_id,
        },
        generationType: wizardSchema.meta.channel,
      },
      {
        onError: (error) => {
          reportError(error)
          setInitialNameLoaded(true) // Even if there's an error, we consider the initial load complete
        },
      },
    )
  }, [initialValues.initial_media_file_id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...props.initialValues }, { assert: false })}
    >
      {({ setFieldValue }) => (
        <>
          <PollFieldsetCompletion
            fieldsetCompletionId={data?.fieldset_completion.id}
            onComplete={(fieldset) => {
              if (fieldset.status === AIJobStatus.Failed) {
                showErrorMessageToast(
                  'Blaze was unable to generate the file name. Please contact support if this issue persists.',
                )
                setInitialNameLoaded(true)
                return
              }

              setFieldValue('filename', fieldset.completed_fields['filename'])
              setInitialNameLoaded(true)
            }}
          />
          <Content {...props} initialNameLoaded={initialNameLoaded} />
        </>
      )}
    </Formik>
  )
})

const Content = multiStep<Props & { initialNameLoaded: boolean }>(
  ({ schema, initialValues, selectedImage, onBack, initialNameLoaded }) => {
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const { isSubmitting, setFieldValue } = useFormikContext()
    const [selectedTemplate, setSelectedTemplate] = useState<VisualElement>()
    const [templateFromModal, setTemplateFromModal] = useState<VisualElement>()
    const { width, height, initial_media_file_remove_background } = initialValues

    const filenameField = Object.entries(schema.fields).find(([name, _]) => name === 'project')
    const aspectRatio = width / height
    let itemWidth = 145
    let itemHeight = 145
    const items = 8

    // ie: pinterest
    if (aspectRatio < 1) {
      itemHeight = 203
      itemWidth = 135
    }

    // ie: vinted
    if (width > height) {
      itemWidth = 145
      itemHeight = 108
    }

    const style: CSS.Properties = {
      ['--preview-width' as any]: `${itemWidth}px`,
      ['--preview-height' as any]: `${itemHeight}px`,
    }

    const filters = {
      element_type: 'Template' as 'Template',
      items,
      width,
      height,
      styles: 'all' as Selection,
      categories: 'all' as Selection,
      types: new Set(['marketplace']),
    }

    const recentQuery = useRecentVisualTemplates(
      { ...filters, items: 4 },
      {
        staleTime: Infinity,
      },
    )
    const recentTemplates = collectResults(recentQuery, (data) => data.visual_elements)
    const query = useVisualTemplates(
      { ...filters, order_by: 'random' },
      {
        staleTime: Infinity,
      },
    )
    const templates = collectResults(query, (data) => data.visual_elements)
    const templatesToDisplay = compact(
      compact(uniqBy([templateFromModal].concat(templates), 'id')).filter(
        (t) => !recentTemplates.map((t) => t.id).includes(t.id),
      ),
    ).slice(0, recentTemplates.length > 0 ? 4 : 8)

    const channel = wizardSchema.meta.channel as DesignChannel
    const isLoading = recentQuery.isLoading || query.isLoading

    const handleSelectTemplate = (template?: VisualElement) => {
      setSelectedTemplate(template)
      setFieldValue('source_visual_element_id', template?.id)
    }

    // Set initial selected template
    useEffect(() => {
      if (templates[0] && !selectedTemplate) handleSelectTemplate(templates[0])
    }, [templates]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Form.Root>
        <Form.Section>
          <div className={Styles.container} style={style}>
            <div className={Styles.templatesRowContainer}>
              <Heading level={4}>Choose a Template</Heading>
              <div className={Styles.templatesRow}>
                {(isLoading || !selectedImage?.variants) && (
                  <>
                    {Array.from({ length: items }).map((_, i) => (
                      <div className={cx(Styles.templatePreviewContainer)} key={`loader-${i}`}>
                        <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} key={i} />
                        <Skeleton className={Styles.thinLine} active paragraph={{ rows: 2 }} title={false} />
                      </div>
                    ))}
                  </>
                )}
                {!isLoading &&
                  selectedImage?.variants &&
                  templatesToDisplay.map((template, i) => {
                    return (
                      <TemplatePreview
                        key={`${template.id}-${i}`}
                        removeBackground={!!initial_media_file_remove_background}
                        template={template}
                        image={selectedImage as IMediaFile}
                        onPress={handleSelectTemplate}
                        isSelected={selectedTemplate?.id === template.id}
                        withLabel
                      />
                    )
                  })}
              </div>
            </div>

            {recentTemplates.length > 0 && !isLoading && (
              <div className={Styles.templatesRowContainer}>
                <Heading level={4}>Or pick one you recently used </Heading>
                <div className={Styles.templatesRow}>
                  {selectedImage?.variants &&
                    recentTemplates.map((recentTemplate, i) => {
                      return (
                        <TemplatePreview
                          key={`${recentTemplate.id}-${i}`}
                          removeBackground={!!initial_media_file_remove_background}
                          template={recentTemplate}
                          image={selectedImage as IMediaFile}
                          onPress={handleSelectTemplate}
                          isSelected={selectedTemplate?.id === recentTemplate.id}
                          withLabel
                        />
                      )
                    })}
                </div>
              </div>
            )}
            <Actions
              width={width}
              height={height}
              channel={channel}
              onTemplateSelect={handleSelectTemplate}
              filters={filters}
              setTemplateFromModal={setTemplateFromModal}
            />

            {filenameField && (
              <BaseField label={filenameField[1].describe().meta.label} isRequired>
                <FilePathFormikField
                  placeholder={filenameField[1].describe().meta.placeholder}
                  locationPickerFullWidth
                  inputClassName={Styles.filenameInput}
                  isNameLoading={!initialNameLoaded}
                />
              </BaseField>
            )}
          </div>
        </Form.Section>
        <Form.Actions isSticky>
          <Form.PreviousButton onPress={onBack} isDisabled={isSubmitting}>
            Back
          </Form.PreviousButton>
          <Form.PrimaryActions>
            <Form.SubmitButton isDisabled={isSubmitting}>{schema.describe().meta.submitLabel}</Form.SubmitButton>
          </Form.PrimaryActions>
        </Form.Actions>
      </Form.Root>
    )
  },
)
